import type { ReactNode } from 'react';

import { cx } from '../lib/cx.ts';

type DisclosureProps = {
  /** The header the user taps. Keep it short — it is a promise about what is inside. */
  title: string;
  /** Sits under the title, closed and open. Use it to say what is in there. */
  hint?: string;
  /** Shown to the right of the title, e.g. a count of what has been filled in. */
  badge?: ReactNode;
  defaultOpen?: boolean;
  children: ReactNode;
  className?: string;
};

/**
 * A collapsible section, closed by default.
 *
 * Built on native `<details>`: the browser gives keyboard operation, the
 * correct expanded/collapsed semantics for a screen reader, and find-in-page
 * that opens the section it lands in. None of that is worth reimplementing.
 *
 * The forms in this app put everything optional behind one of these, so the
 * common path stays short. See the friction rule in CLAUDE.md.
 */
export function Disclosure({
  title,
  hint,
  badge,
  defaultOpen = false,
  children,
  className,
}: DisclosureProps) {
  return (
    <details
      open={defaultOpen}
      className={cx('group rounded-md border border-line bg-surface', className)}
    >
      <summary
        className={cx(
          'flex min-h-touch cursor-pointer list-none items-center justify-between gap-3 px-4 py-2',
          'text-sm font-medium text-text select-none',
          '[&::-webkit-details-marker]:hidden',
        )}
      >
        <span className="flex flex-col">
          {title}
          {hint === undefined ? null : (
            <span className="text-xs font-normal text-faint">{hint}</span>
          )}
        </span>

        <span className="flex shrink-0 items-center gap-2 text-xs text-faint">
          {badge}
          <span
            aria-hidden="true"
            className="transition-transform duration-(--ui-duration-fast) group-open:rotate-180"
          >
            ▾
          </span>
        </span>
      </summary>

      <div className="flex flex-col gap-4 border-t border-line p-4">{children}</div>
    </details>
  );
}
